import { Button } from '@creditornot/cb-button';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import validateYupSchema from 'common/utils/validateYupSchema';

import { Summary } from './Summary';
import { DataAggregationLevelFormItem } from './form/DataAggregationFormItem';
import { ReportFrequencyFormItem } from './form/ReportFrequencyFormItem';
import { InsightsReportTypeFormItem } from './form/ReportTypeFormItem';
import { DataOptionsFormItem } from './form/data-options/DataOptionsFormItem';
import { insightsReportFormValidationSchema } from './form/validation';
import { VenuesFormItem } from './form/venues/VenuesFormItem';
import { DataAggregationLevel, InsightReportForm, InsightsReportType, ReportDataOption } from './types';

type ReportFormProps = {
  venues: Venue[];
};

const ReportFormContainer = styled.div`
  top: 16px;
  display: flex;
  width: 100%;
  max-width: 1200px;
  gap: 40px;
  padding-block: 40px;
  padding-inline: 20px;
  align-items: start;
  margin: 0 auto;

  @media (width < ${MOBILE_BREAKPOINT}px) {
    padding-block-end: 150px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 70%;
  min-width: auto;
  @media (width < ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
  @media (width > 1200px) {
    flex: 1 1 700px;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 16px;
  width: 30%;
  min-width: 300px;
  border: 1px solid ${cssThemedValue('borderPrimary')};
  border-radius: 8px;

  @media (width > 1200px) {
    flex: 1 1 380px;
  }
  @media (width< ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

export const ReportForm = ({ venues }: ReportFormProps) => {
  const isMobile = useIsMobile();

  const initialValues: Partial<InsightReportForm> = useMemo(
    () => ({
      type: InsightsReportType.PERFORMANCE,
      dataAggregationLevel: DataAggregationLevel.MONTHLY,
      dataOptions: Object.values(ReportDataOption),
    }),
    [],
  );

  return (
    <ReportFormContainer>
      <Form<InsightReportForm>
        onSubmit={() => () => null}
        initialValues={initialValues}
        validate={values =>
          validateYupSchema(insightsReportFormValidationSchema, {
            type: values.type,
          })(values)
        }
      >
        {({ handleSubmit, form, values }) => {
          // TODO: @navjotahuja92 derive data options from report type and aggregation level
          const dataOptions = Object.values(ReportDataOption);
          return (
            <>
              <Content>
                <InsightsReportTypeFormItem />
                {![InsightsReportType.SETTLEMENTS, InsightsReportType.RATINGS].includes(values.type) && (
                  <DataAggregationLevelFormItem />
                )}
                <VenuesFormItem
                  initialValues={{
                    venueIds: values.venueIds ?? [],
                  }}
                  venues={venues}
                  onUpdate={(venueIds: string[]) => form.change('venueIds', venueIds)}
                />
                <DataOptionsFormItem dataOptions={dataOptions} />
                <ReportFrequencyFormItem />
                {!isMobile && (
                  <Button variant="blue" size="large" onClick={handleSubmit}>
                    Create Report
                  </Button>
                )}
              </Content>
              <Sidebar>
                <Summary />
              </Sidebar>
              {isMobile && (
                <Button variant="blue" size="large" onClick={handleSubmit}>
                  <FormattedMessage id="insights.reports.action" />
                </Button>
              )}
            </>
          );
        }}
      </Form>
    </ReportFormContainer>
  );
};
